import { Box, Center, Divider, Flex, Loader, Stack, Text } from '@mantine/core';
import MarkdownEditor from 'src/common/components/markdown-editor/MarkdownEditor';
import useTemplates, {
  TemplateWithoutSections,
} from 'src/data-capture/hooks/templates/useTemplates';
import { useCalendarEventMeetingNote } from 'src/meetings/hooks/useCalendarEventMeetingNote';
import { useGenerateCalendarEventMeetingNote } from 'src/meetings/hooks/useGenerateCalendarEventMeetingNote';
import type { CalendarEvent } from 'src/meetings/models';
import PageLoader from 'src/UI/loader/PageLoader';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { DownloadFileButton } from 'src/common/components/buttons/DownloadFileButton';

type Props = {
  event: CalendarEvent;
};

export const MeetingNoteTab = ({ event }: Props) => {
  const { files: templates, isLoading: isLoadingTemplates } = useTemplates({
    templateType: 'MEETING_NOTE',
  });

  const {
    meetingNote,
    isLoading: isLoadingNote,
    refetch,
  } = useCalendarEventMeetingNote(event.id, { enabled: event.hasMeetingNote });

  const { generateMeetingNote, isLoading: isGenerating } = useGenerateCalendarEventMeetingNote(
    event.id,
  );

  const handleSelectTemplate = async (templateId: string) => {
    try {
      await generateMeetingNote({ templateId });
      refetch();
    } catch (error) {
      notifyAndLogError('Failed to generate meeting note', 'Please try again later', error, {
        eventId: event.id,
        templateId,
      });
    }
  };

  const isLoading = isLoadingTemplates || (event.hasMeetingNote && isLoadingNote);

  if (isLoading) {
    return <PageLoader />;
  }

  if (event.hasMeetingNote && meetingNote) {
    return (
      <Box>
        <Divider color="gray.1" mt={'sm'} mb={'xxxs'} />
        <DownloadFileButton variant={'subtle'} fileId={meetingNote.templatisedFileId!} />
        <Divider color="gray.1" mb={'sm'} mt={'xxxs'} />
        <MarkdownEditor
          defaultValue={meetingNote.markdownContent}
          readonly={true}
          h="100%"
          withBorder={false}
          style={{ borderWidth: 0 }}
        />
      </Box>
    );
  }

  if (isGenerating) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  if (!event.hasTranscription) {
    return (
      <Box p="md">
        <Text size="sm" c="gray.7">
          A transcription is required to generate a meeting note.
        </Text>
      </Box>
    );
  }

  return (
    <Stack gap="xs">
      <Text size="textMd" fw={FONT_WEIGHTS.semibold} c="gray.10">
        Select template to generate a meeting note
      </Text>
      <Stack gap="xxs">
        {templates.map(template => (
          <TemplateCard
            key={template.id}
            template={template}
            onSelect={() => handleSelectTemplate(template.id)}
          />
        ))}
      </Stack>
    </Stack>
  );
};

type TemplateCardProps = {
  template: TemplateWithoutSections;
  onSelect: () => void;
};

const TemplateCard = ({ template, onSelect }: TemplateCardProps) => {
  return (
    <Flex
      bg="gray.1"
      style={{ borderRadius: '16px', cursor: 'pointer' }}
      py="sm"
      px="md"
      justify="space-between"
      align="center"
      onClick={onSelect}
    >
      <Stack gap="xxxs">
        <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.10">
          {template.displayName}
        </Text>
        <Text size="textXs" fw={FONT_WEIGHTS.regular} c="gray.10">
          {template.description}
        </Text>
      </Stack>
      <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="brand.6">
        Select
      </Text>
    </Flex>
  );
};
